/**
 * InpiRedirectionSignatureController — page intermédiaire
 * `/inpi-redirection-signature.html` (EPIC-008 / 8.7).
 *
 * Affiche un message explicatif sur la signature qualifiée requise par
 * l'INPI pour les modifications, puis pousse la formalité au Guichet
 * Unique au clic « Continuer » (`POST /inpi/modification/{id}/sign`)
 * et redirige le navigateur vers l'URL profonde renvoyée
 * (`procedures.inpi.fr/formalites/{inpiId}/signature`).
 *
 * Conventions :
 *   - Pas d'HTML inline en JS.
 *   - Aucune classe CSS inventée.
 *   - Vocabulaire UI : « INPI », « procedures.inpi.fr », « FranceConnect+ »,
 *     « eIDAS » visibles ; pas de NotaFacto/Sweego/SuperPDP.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const ERROR_MESSAGES = {
  modification_invalid_state: "Cette formalité a déjà été signée ou n'est plus en attente.",
  inpi_api_unreachable: "Le service INPI est temporairement indisponible. Réessayez dans quelques instants.",
  inpi_authentication_failed: "Notre connexion à l'INPI est en échec. Notre équipe a été alertée.",
  inpi_invalid_payload: "L'INPI a refusé le brouillon. Notre équipe a été alertée.",
  formality_not_found: "Cette formalité est introuvable.",
};

export default class InpiRedirectionSignatureController extends BaseController {
  static targets = ["continueBtn", "error", "errorMessage"];

  _formalityId = null;

  connect() {
    this._formalityId = new URLSearchParams(window.location.search).get("formalityId");
    if (!this._formalityId) {
      this._showError("Paramètre `formalityId` manquant dans l'URL.");
      if (this.hasContinueBtnTarget) this.continueBtnTarget.disabled = true;
    }
  }

  async continueToInpi() {
    if (!this._formalityId) return;
    if (!this.hasContinueBtnTarget) return;

    this._clearError();
    this.continueBtnTarget.disabled = true;
    const originalLabel = this.continueBtnTarget.textContent;
    this.continueBtnTarget.textContent = "Préparation de votre signature…";

    try {
      const data = await api.inpi.modificationSign(this._formalityId);
      const url = data?.redirectUrl;
      if (!url) {
        throw new Error("Réponse inattendue du serveur (URL manquante).");
      }
      // Redirection complète — on quitte le SPA pour le portail INPI.
      window.location.href = url;
    } catch (err) {
      console.error("Failed to sign INPI formality", err);
      const msg = this._extractMessage(err) || "Impossible de générer le lien de signature.";
      this._showError(msg);
      this.continueBtnTarget.disabled = false;
      this.continueBtnTarget.textContent = originalLabel;
    }
  }

  _showError(message) {
    if (this.hasErrorTarget) this.errorTarget.hidden = false;
    if (this.hasErrorMessageTarget) this.errorMessageTarget.textContent = " " + message;
  }

  _clearError() {
    if (this.hasErrorTarget) this.errorTarget.hidden = true;
    if (this.hasErrorMessageTarget) this.errorMessageTarget.textContent = "";
  }

  _extractMessage(err) {
    const code = err?.data?.error?.code;
    if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
    if (err?.data?.error?.message) return err.data.error.message;
    if (err?.message) return err.message;
    return null;
  }
}
